import React, { useEffect, useMemo, useState } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { Plus, Search, Package, Wrench, Pencil, Copy, Trash2, GripVertical, Sparkles, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { listCatalog, deleteCatalogItem, duplicateCatalogItem, reorderCatalog } from "../lib/api";
import { usePlan } from "../context/PlanContext";
import CatalogItemDialog from "./CatalogItemDialog";
import CatalogImportDialog from "./CatalogImportDialog";

export const fmtPrice = (p, unit) => p == null ? "Sob consulta" : `R$ ${Number(p).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}${unit ? ` / ${unit}` : ""}`;

const Thumb = ({ item, size = "w-14 h-14" }) => (
  <div className={`${size} shrink-0 rounded-xl overflow-hidden bg-slate-100 grid place-items-center text-slate-300`}>
    {item.image ? <img src={item.image} alt="" className="w-full h-full object-cover" /> : item.kind === "product" ? <Package size={20} /> : <Wrench size={20} />}
  </div>
);

export default function Catalog({ companyId, companyName }) {
  const { openUpgrade } = usePlan();
  const [data, setData] = useState({ items: [], categories: [], total: 0, limit: null });
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [kind, setKind] = useState("");
  const [dialog, setDialog] = useState(null); // null | {item}
  const [importOpen, setImportOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { setData(await listCatalog(companyId)); } catch { toast.error("Não foi possível carregar o catálogo"); } finally { setLoading(false); }
  };
  useEffect(() => { setLoading(true); load(); }, [companyId]); // eslint-disable-line

  const filtered = useMemo(() => {
    const nq = q.trim().toLowerCase();
    return data.items.filter((i) => (!cat || i.category === cat) && (!kind || i.kind === kind) &&
      (!nq || `${i.name} ${i.description || ""} ${i.category || ""} ${(i.synonyms || []).join(" ")}`.toLowerCase().includes(nq)));
  }, [data.items, q, cat, kind]);
  const canReorder = !q && !cat && !kind;

  const onNew = () => {
    if (data.limit != null && data.total >= data.limit) return openUpgrade("catalog");
    setDialog({ item: null });
  };
  const onSaved = (saved) => setData((d) => {
    const exists = d.items.some((i) => i.id === saved.id);
    return { ...d, items: exists ? d.items.map((i) => (i.id === saved.id ? saved : i)) : [...d.items, saved], total: exists ? d.total : d.total + 1,
      categories: Array.from(new Set([...d.categories, saved.category].filter(Boolean))).sort() };
  });
  const onDuplicate = async (it) => {
    try { await duplicateCatalogItem(companyId, it.id); toast.success("Item duplicado"); load(); }
    catch (err) { if (err?.response?.data?.detail?.code === "catalog_limit") openUpgrade("catalog"); else toast.error("Não foi possível duplicar"); }
  };
  const onDelete = async (it) => {
    if (!window.confirm(`Excluir "${it.name}" do catálogo?`)) return;
    try { await deleteCatalogItem(companyId, it.id); setData((d) => ({ ...d, items: d.items.filter((i) => i.id !== it.id), total: d.total - 1 })); toast.success("Item excluído"); }
    catch { toast.error("Não foi possível excluir"); }
  };
  const onReorder = (items) => { setData((d) => ({ ...d, items })); reorderCatalog(companyId, items.map((i) => i.id)).catch(() => {}); };

  return (
    <div className="min-h-screen pt-14 px-5"><div className="max-w-5xl mx-auto pt-10 pb-20 float-up" data-testid="catalog-view">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Catálogo</h1>
          <p className="mt-1 text-[13.5px] text-slate-500">Produtos e serviços de <span className="font-medium text-slate-700">{companyName}</span>. A IA reconhece esses itens nos seus pedidos e usa descrição e preço automaticamente.{data.limit != null && <span className="ml-1 text-slate-400">({data.total}/{data.limit} no plano gratuito)</span>}</p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button onClick={() => setImportOpen(true)} variant="outline" data-testid="catalog-import-btn" className="rounded-full h-10 px-4 border-slate-200 bg-white text-slate-700 text-[13.5px] gap-1.5"><Upload size={15} /> Importar</Button>
          <Button onClick={onNew} data-testid="catalog-new-btn" className="rounded-full h-10 px-5 bg-slate-900 hover:bg-slate-800 text-[13.5px] gap-1.5"><Plus size={16} /> Novo item</Button>
        </div>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pesquisar por nome, descrição ou categoria…" data-testid="catalog-search"
            className="w-full h-10 rounded-full bg-white ring-1 ring-slate-200 focus:ring-2 focus:ring-slate-900 pl-10 pr-4 text-[13.5px] outline-none transition-shadow" />
        </div>
        <div className="flex rounded-full bg-slate-100 p-1 text-[12.5px] font-medium shrink-0">
          {[["", "Todos"], ["service", "Serviços"], ["product", "Produtos"]].map(([v, l]) => (
            <button key={v} onClick={() => setKind(v)} data-testid={`catalog-kind-filter-${v || "all"}`} className={`h-8 px-3 rounded-full transition-colors ${kind === v ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>{l}</button>
          ))}
        </div>
      </div>
      {data.categories.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="catalog-categories">
          <button onClick={() => setCat("")} className={`h-7 px-3 rounded-full text-[12px] ring-1 transition-colors ${!cat ? "bg-slate-900 text-white ring-slate-900" : "text-slate-600 ring-slate-200 hover:bg-slate-50"}`}>Todas</button>
          {data.categories.map((c) => (
            <button key={c} onClick={() => setCat(cat === c ? "" : c)} data-testid={`catalog-cat-${c}`} className={`h-7 px-3 rounded-full text-[12px] ring-1 transition-colors ${cat === c ? "bg-slate-900 text-white ring-slate-900" : "text-slate-600 ring-slate-200 hover:bg-slate-50"}`}>{c}</button>
          ))}
        </div>
      )}

      {loading ? <div className="mt-10 text-center text-[13px] text-slate-400">Carregando…</div> : !data.items.length ? (
        <div className="mt-10 rounded-3xl ring-1 ring-slate-200 bg-white p-10 text-center" data-testid="catalog-empty">
          <div className="mx-auto w-12 h-12 rounded-2xl bg-[#F4F5FF] text-[#4A3BFA] grid place-items-center"><Sparkles size={20} /></div>
          <h2 className="mt-4 text-[16px] font-semibold text-slate-900">Seu catálogo está vazio — e tudo bem</h2>
          <p className="mt-1.5 text-[13px] text-slate-500 max-w-md mx-auto">Você pode continuar criando orçamentos livremente. Cadastre seus serviços e produtos mais comuns para a IA preencher descrição e preço sozinha, sem você repetir.</p>
          <Button onClick={onNew} data-testid="catalog-empty-new" className="mt-5 rounded-full h-10 px-5 bg-slate-900 hover:bg-slate-800 text-[13.5px] gap-1.5"><Plus size={16} /> Cadastrar primeiro item</Button>
        </div>
      ) : !filtered.length ? (
        <div className="mt-10 text-center text-[13px] text-slate-400" data-testid="catalog-no-results">Nada encontrado para essa busca.</div>
      ) : (
        <Reorder.Group axis="y" values={filtered} onReorder={canReorder ? onReorder : () => {}} className="mt-5 space-y-2" data-testid="catalog-list">
          {filtered.map((it) => (
            <CatalogRow key={it.id} it={it} canReorder={canReorder} onEdit={() => setDialog({ item: it })} onDuplicate={() => onDuplicate(it)} onDelete={() => onDelete(it)} />
          ))}
        </Reorder.Group>
      )}
      {!loading && data.items.length > 0 && !canReorder && <p className="mt-3 text-[11.5px] text-slate-400">Limpe a busca e os filtros para reordenar arrastando.</p>}

      <CatalogItemDialog open={!!dialog} onClose={() => setDialog(null)} companyId={companyId} item={dialog?.item} categories={data.categories} onSaved={onSaved} />
      <CatalogImportDialog open={importOpen} onClose={() => setImportOpen(false)} companyId={companyId} onDone={load} />
    </div></div>
  );
}

function CatalogRow({ it, canReorder, onEdit, onDuplicate, onDelete }) {
  const controls = useDragControls();
  return (
    <Reorder.Item value={it} dragListener={false} dragControls={controls} drag={canReorder ? "y" : false} className="group rounded-2xl bg-white ring-1 ring-slate-200 hover:ring-slate-300 transition-shadow p-3 flex items-center gap-3" data-testid={`catalog-item-${it.id}`}>
      {canReorder && <span className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 touch-none" onPointerDown={(e) => controls.start(e)} data-testid={`catalog-drag-${it.id}`}><GripVertical size={16} /></span>}
      <Thumb item={it} />
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="text-[14px] font-semibold text-slate-900 truncate">{it.name}</span>
          <span className="text-[10.5px] uppercase tracking-wider text-slate-400">{it.kind === "product" ? "Produto" : "Serviço"}</span>
          {it.category && <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{it.category}</span>}
        </div>
        {it.description && <p className="text-[12.5px] text-slate-500 line-clamp-1 mt-0.5">{it.description}</p>}
      </div>
      <div className={`text-[13.5px] font-semibold shrink-0 ${it.price == null ? "text-slate-400 font-medium" : "text-slate-900"}`} data-testid={`catalog-price-${it.id}`}>{fmtPrice(it.price, it.unit)}</div>
      <div className="flex items-center gap-0.5 shrink-0 opacity-70 group-hover:opacity-100 transition-opacity">
        <button onClick={onEdit} title="Editar" data-testid={`catalog-edit-${it.id}`} className="grid place-items-center w-8 h-8 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100"><Pencil size={14} /></button>
        <button onClick={onDuplicate} title="Duplicar" data-testid={`catalog-duplicate-${it.id}`} className="grid place-items-center w-8 h-8 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100"><Copy size={14} /></button>
        <button onClick={onDelete} title="Excluir" data-testid={`catalog-delete-${it.id}`} className="grid place-items-center w-8 h-8 rounded-lg text-slate-500 hover:text-rose-600 hover:bg-rose-50"><Trash2 size={14} /></button>
      </div>
    </Reorder.Item>
  );
}
